import { useEffect, useMemo, useRef, useState } from "react";

import styles from "./list.module.css";

import { CoursCard, DateTimeDisplay, Icons, ReactIcons, Text } from "@kalyzee/escaet-ui";
import { useTimeEventsOfTheDay } from "@kalyzee/kast-web-sdk";
import { MeetingOutputDTO, TimeEventOutputDTO, TimeEventTargetModels } from "@kalyzee/kast-web-sdk/http";
import { isValidHttpUrl, Touchable, useTriggerable } from "@kalyzee/kast-web-sdk/web-components";
import { isToday } from "date-fns";
import DatePicker from "react-datepicker";
import { PageMasterMode, usePageContextRef } from "../../../../components/navigation/PageContext";
import { ErrorView } from "../../../../components/utils/ErrorView";
import ExpertSettingsPopup from "../../../../components/utils/ExpertSettingsPopup";
import { LoadingView } from "../../../../components/utils/LoadingView";
import { TimeEventsAgenda } from "../../../../components/utils/TimeEventsAgenda";
import { getFullName } from "../../../../helpers/meeting";
import { addPopup, removePopupByGroup, removePopupByTag } from "../../../../helpers/popup";
import { sdkClient } from "../../../../helpers/sdk";
import { toastError } from "../../../../helpers/toast";
import { useSettings } from "../../../../hooks/settings";
import { SessionMode } from "../../../../store/session/slices";
import { getExpertHomeParams } from "../../../../helpers/storage";
import { appMessage } from "../../../../helpers/message";

const AUTO_EVENT_SELECTION_FAVORED_AFTER_A_DELAY = 10 * 60 * 1000; // 10 minutes

export const MeetingListExpertPage = () => {
  const pageContextRef = usePageContextRef<SessionMode.MASTER>();
  const [date, setDate] = useState<Date>(new Date());

  const [selectedEventId, setSelectedEventId] = useState<string>();
  const selectedEventAtRef = useRef<Date>();
  const logoContainerRef = useRef<HTMLImageElement>(null);
  const courseCardContainerRef = useRef<HTMLImageElement>(null);
  const devContainerRef = useRef<HTMLImageElement>(null);

  const [settings] = useSettings();
  const roomId = useMemo(() => {
    return new URLSearchParams(window.location.search).get("roomId") ?? settings.roomId;
  }, [settings]);
  const meetingId = pageContextRef.current?.searchParams?.get("meeting");

  const [mode, setMode] = useState<"meeting" | "list">(meetingId ? "meeting" : "list");
  const { room, timeEvents, currentTimeEvents, error, retry } = useTimeEventsOfTheDay(sdkClient, date, roomId ? {type: "room", id: roomId } : undefined, false, {
    meeting: mode === "meeting" ? meetingId ?? undefined : undefined,
    targets: [TimeEventTargetModels.Meeting],
    populate: { room: settings.dev, actor: { identity: true } },
  });
  const currentTimeEvent = useMemo(() => currentTimeEvents.length ? currentTimeEvents[0] : undefined, [currentTimeEvents]);

  useTriggerable(logoContainerRef, { clickCount: 3 }, () => pageContextRef.current?.openSettingsPopup()); // press click 3 times with mouse(left button) on logo
  useTriggerable(devContainerRef, { clickCount: 3 }, () => {
    // DEBUG
    let selectedEvent: TimeEventOutputDTO | undefined;
    if (mode === "meeting") selectedEvent = timeEvents?.length ? timeEvents[0] : undefined;
    else selectedEvent = timeEvents?.find((event) => event.id === selectedEventId);
    const selectedMeeting = selectedEvent ? (selectedEvent.target as MeetingOutputDTO) : undefined;
    if (!selectedMeeting) {
      toastError("Impossible de récupérer la liste des events");
      return;
    }
    const url = `${sdkClient.getHttpClient().getBaseURL()}${sdkClient.getHttpClient().getBaseURL().endsWith("/") ? "" : "/"}escaet/events/${
      selectedMeeting.id
    }`;
    const openCsvInNewTab = async (url: string) => {
      try {
        // Récupérer le fichier CSV
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error("Erreur lors du chargement du fichier CSV");
        }

        const csvText = await response.text();
        const blob = new Blob([csvText], { type: "text/csv" });
        const blobUrl = URL.createObjectURL(blob);
        const newTab = window.open();
        let maxColumns = 0;
        const lines = csvText
          .replace(/</g, "&lt;")
          .split("\n")
          .map((l) => {
            const columns = l.split(";");
            let isEmpty = true;
            while (columns.length > 0 && isEmpty) {
              if (!columns[columns.length - 1].trim().length) {
                isEmpty = true;
                columns.pop();
              } else {
                isEmpty = false;
              }
            }
            maxColumns = Math.max(maxColumns, columns.length);
            return columns;
          });
        const body = `
          <button onclick="(() => {console.log('mdr');window.open('${blobUrl}', '_blank');})()">Download</button>
          <div style="display: flex; width: 100%; flex-direction: column;">
            ${lines
              .map((l, index) => {
                const elements = l.map((e) => {
                  let color = "black";
                  if (l.includes(";connect")) color = "green";
                  else if (l.includes(";disconnect")) color = "red";
                  const isLink = isValidHttpUrl(e);
                  if (isLink) return `<a href="${e}" target="_blank" style="flex: 1; min-width: 0px; text-align: center; color: ${color}">${e}</a>`;
                  return `<div style="flex: 1; min-width: 0px; text-align: center; color: ${color}">${e}</div>`;
                });
                while (elements.length < maxColumns) elements.push('<div style="flex: 1; min-width: 0px"></div>');
                return `<div style="display: flex; width: 100%; flex-direction: row;background-color: ${index % 2 ? "#00000011" : "#00000000"}">${elements.join(
                  ""
                )}</div>`;
              })
              .join("")}
          </div>
        `;
        newTab?.document.write(body);
        newTab?.document.close();
      } catch (error) {
        toastError("Impossible de récupérer la liste des events");
        console.error("Erreur :", error);
      }
    };
    openCsvInNewTab(url);
  });

  // ---------------------------------------------------------------------- //
  // ---------------------------   UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  // ---------------------------------------------------------------------- //
  // ---------------------------   EFFECT ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    const expertHomeParams = getExpertHomeParams();
    const micOffOutside = expertHomeParams?.micOffOutsideOfAnEvent ?? false;
    appMessage.setMicrophoneData(micOffOutside, micOffOutside);
    return () => {
      removePopupByGroup("list"); // clean all popups
    };
  }, []);

  useEffect(() => {
    if (!isToday(date)) return;
    // // check date every minute
    const interval = setInterval(() => {
      if (!isToday(date.getDate())) {
        setDate(new Date());
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [date]);

  useEffect(() => {
    const now = new Date();
    if (!selectedEventId && currentTimeEvent) setSelectedEventId(currentTimeEvent._id);
    else if (selectedEventAtRef.current) {
      const updateAfter = selectedEventAtRef.current.getTime() + AUTO_EVENT_SELECTION_FAVORED_AFTER_A_DELAY - now.getTime();
      if (updateAfter > 0) {
        const timeout = setTimeout(() => {
          if (currentTimeEvent) setSelectedEventId(currentTimeEvent._id);
        }, updateAfter);
        return () => {
          clearTimeout(timeout);
        };
      } else {
        if (currentTimeEvent) setSelectedEventId(currentTimeEvent._id);
      }
    }
  }, [currentTimeEvent, selectedEventId]);

  useEffect(() => {
    if (!selectedEventId) return;
    selectedEventAtRef.current = new Date();
  }, [selectedEventId]);


  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //

  const renderCourseCard = () => {
    let selectedEvent: TimeEventOutputDTO | undefined;
    if (mode === "meeting") selectedEvent = timeEvents?.length ? timeEvents[0] : undefined;
    else selectedEvent = timeEvents?.find((event) => event.id === selectedEventId);
    const selectedMeeting = selectedEvent ? (selectedEvent.target as MeetingOutputDTO) : undefined;

    const renderNoEventCard = () => {
      if (mode === "meeting") {
        return (
          <div className={styles.noEventCard}>
            <p>L'event ne semble pas exister</p>
          </div>
        );
      }
      if (timeEvents?.length) return null;
      return (
        <div className={styles.noEventCard}>
          <p>Veuillez sélectionner un événement</p>
        </div>
      );
    };

    const renderContent = () => {
      if (!selectedEvent || !selectedMeeting) {
        return renderNoEventCard();
      }
      const expert = selectedMeeting.actors.find((actor) => actor.externalProvider === "escaet:expert");
      const { firstname, lastname, avatar } = expert?.identity ?? {};
      const fullname = getFullName(firstname, lastname);
      const promos: string[] = (selectedMeeting as any).promos;
      return (
        <>
          {settings.dev ? (
            <div
              style={{
                width: "100%",
                padding: "10px",
                background: "white",
                borderRadius: "15px",
                marginBottom: "10px",
                display: "flex",
                flexDirection: "column",
                gap: "5px",
              }}
            >
              <div style={{ width: "100%", color: "black", textAlign: "center", fontWeight: "bold" }}>Dev</div>
              <div style={{ width: "100%", color: "black" }}>{`id: ${selectedMeeting._id}`}</div>
              <div style={{ width: "100%", color: "black" }}>{`room: ${(selectedEvent as any).room ? `${(selectedEvent as any).room.name} - ` : ""}${
                selectedEvent._room
              }`}</div>
              <div style={{ width: "100%", color: "black" }}>{`lessions: `}</div>
              {(selectedMeeting as any).lessons?.length ? (
                (selectedMeeting as any).lessons?.map((l: any) => {
                  return (
                    <div
                      key={`dev-lesson-${l.id}`}
                      style={{ width: "100%", color: "black", paddingLeft: "10px" }}
                    >{`- id: ${l.id}  -  promo: ${l.promo} - formation: ${l.formationId} - module: ${l.moduleId}`}</div>
                  );
                })
              ) : (
                <div style={{ width: "100%", color: "black", paddingLeft: "10px" }}>pas de lesson associé</div>
              )}
              <div style={{ width: "100%", color: "black" }}>{`state: ${(selectedMeeting as any).state}`}</div>
              <div style={{ width: "100%", color: "black" }}>{`appel: ${selectedMeeting.callWasDone}`}</div>
              <div style={{ width: "100%", color: "black" }}>
                events:
                <a
                  style={{ marginLeft: "10px", color: "blue", textDecorationLine: "underline" }}
                  target="_blank"
                  rel="noreferrer"
                  href={`${sdkClient.getHttpClient().getBaseURL()}${sdkClient.getHttpClient().getBaseURL().endsWith("/") ? "" : "/"}escaet/events/${
                    selectedMeeting.id
                  }`}
                >
                  file.csv
                </a>
              </div>
              <div style={{ width: "100%", color: "black", display: "flex", flexDirection: "row", alignItems: "center" }}>
                vods:
                <Touchable
                  style={{ marginLeft: "10px", color: "blue", textDecorationLine: "underline" }}
                  target="_blank"
                  rel="noreferrer"
                  onPress={() => {
                    pageContextRef.current?.goTo(PageMasterMode.VOD, { id: selectedMeeting.id }, { new: true });
                  }}
                >
                  link
                </Touchable>
              </div>
            </div>
          ) : null}
          <CoursCard
            rooms={[]}
            isMeetOpen={false}
            title="Cours"
            teacherIcon={avatar}
            teacherName={fullname}
            roomName={promos ? promos.join("/") : ""}
            description={selectedMeeting.name ?? ""}
            onStart={() => {
              pageContextRef.current?.goTo(PageMasterMode.MEETING_DASHBOARD, { id: selectedMeeting._id });
            }}
            style={{flex: 1, minHeight: 0}}
          />
          {
            // DEV get events
            <div
              ref={devContainerRef}
              style={{ position: "absolute", left: 0, top: settings.dev ? 300 : 0, bottom: 150, width: "100%", background: "#00000000" }}
            />
          }
        </>
      );
    };

    return (
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: "0.5em",
          flex: 1,
          minHeight: 0,
        }}
        ref={courseCardContainerRef}
      >
        {renderContent()}
        {mode === "meeting" ? (
          <Touchable
            onPress={() => {
              setMode("list");
              pageContextRef.current?.goTo(PageMasterMode.LIST, undefined);
            }}
            style={{ width: "100%", textAlign: "center", color: "black", fontSize: "1rem" }}
          >
            Revenir à la liste
          </Touchable>
        ) : null}
      </div>
    );
  };

  if (error) return <ErrorView retry={() => { retry(); }} />;
  if (roomId && !room) return <LoadingView />;

  return (
    <div className={styles.mainContainer}>
      <div className={styles.meetingListContainer}>
        <div className={styles.meetingListLeftPane}>
          <div ref={logoContainerRef}>
            <img ref={logoContainerRef} src={Icons.PedagoLogoSamii} alt="logo" />
          </div>
          {mode === "list" ? (
            <div>
              <Text
                value={room ? `Agenda de la salle ${room.name}` : "Agenda"}
                style={{
                  fontSize: "1.5em",
                  fontWeight: 700,
                  color: "#888",
                  marginBottom: "0.5em",
                  width: "fit-content",
                }}
              />
              {settings.dev ? (
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                    marginBottom: "5px",
                    backgroundColor: "#00000008",
                    borderRadius: "5px",
                    padding: "5px",
                  }}
                >
                  <DatePicker
                    showIcon
                    selected={date}
                    date={date ?? undefined}
                    onChange={(d) => {
                      if (!d) return;
                      setDate(d);
                    }}
                  />
                </div>
              ) : null}
              <TimeEventsAgenda
                timeEvents={timeEvents?.filter((event) => (event.target ? true : false)) ?? []}
                selectedEventId={selectedEventId}
                onSelectEvent={setSelectedEventId}
                kind="expert"
              />
            </div>
          ) : null}
          <DateTimeDisplay />
        </div>
        <div className={styles.meetingListRightPane}>
          {renderCourseCard()}
          <Touchable
              style={{ padding: 15, borderRadius: "50%", background: "#DDDDDD" }}
              onPress={() => {
                const popupTag = "settings";
                removePopupByTag(popupTag); /// remove if already exist
                const popup = <ExpertSettingsPopup onClose={() => removePopupByTag(popupTag)} />;
                addPopup(popup, popupTag, "list");
              }}
            >
              <ReactIcons.Settings2 width="30" height="30" fill="#222222" />
            </Touchable>
        </div>
      </div>
    </div>
  );
};
